<template>
<Form
	ref="form"
	class="m-0 p-0 g-0"
	@submit="onSubmit"
	@invalid-submit="console.log"
	:validation-schema="schema"
	:initial-values="formValues"
	v-slot="{ errors, meta, isSubmitting, }"
>
<fieldset class="" :disabled="!isFormEnabled">
	<div style="display: none;">
		{{ formMeta = meta }}
		{{ isSubmittingForm = isSubmitting }}
	</div>

	<div class="form-row row m-0 p-0 g-3">
		<div class="col-12 mt-1">
			<label class="fw-bold">
				Первинні налаштування
			</label>
		</div>
		
		<div class="col-12 col-xl-6">
			<label>{{ PowerUnit.settingNames[PowerUnit.passportKeys.unitVersion] }}</label>
			<Field
				:name="Station.passportKeys.powerUnit.unitVersion"
				as="select"
				class="form-control form-control-sm"
				:class="{
					'is-invalid': errors[Station.passportKeys.powerUnit.unitVersion]
				}"
			>
				<option value="" disabled>Натисніть щоб обрати</option>
				<template v-for="[key, value] of Object.entries(PowerUnit.versionNames)" :key="key">
					<option :value="key">{{ value }}</option>
				</template>
			</Field>
			<div class="invalid-feedback">
				{{errors[Station.passportKeys.powerUnit.unitVersion]}}
			</div>
		</div>

		<div class="col-12 col-xl-6 d-none d-xl-block">
			&nbsp;
		</div>
	</div>
		
	<div class="form-row row m-0 p-0 g-3">
		<div class="col-12 col-xl-6">
			<label>{{ PowerUnit.settingNames[PowerUnit.passportKeys.unitPort] }}</label>
			<Field
				:name="Station.passportKeys.powerUnit.unitPort"
				type="number"
				class="form-control form-control-sm no-arrow"
				:class="{
					'is-invalid': errors[Station.passportKeys.powerUnit.unitPort]
				}"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.powerUnit.unitPort] }}
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					Якщо порт співпадає з налаштуваннями модема "{{ ModemUnit.settingNames[Station.passportKeys.modemUnit.server1Port1] }}" або 
					"{{ ModemUnit.settingNames[Station.passportKeys.modemUnit.server2Port1] }}", 
					то для опитування модуля застосовується протокол відповідно до налаштування відповідного порта модема 
					"{{ ModemUnit.settingNames[Station.passportKeys.modemUnit.convertingRtu1] }}" або 
					"{{ ModemUnit.settingNames[Station.passportKeys.modemUnit.convertingRtu2] }}".<br>
				</p>
				<p class="form-text fst-italic paragraph-indent">
					Якщо порт не співпадає, то протокол ModBus TCP.
				</p>
			</div>
		</div>
	</div>

	<div class="form-row row m-0 p-0 g-3">
		<div class="col-12 col-xl-6">
			<label>{{ PowerUnit.settingNames[PowerUnit.passportKeys.unitModbusAddr] }}</label>
			<Field
				:name="Station.passportKeys.powerUnit.unitModbusAddr"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[Station.passportKeys.powerUnit.unitModbusAddr] }"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.powerUnit.unitModbusAddr] }}
			</div>
		</div>

		<div class="col-12 col-xl-6 d-none d-xl-block">
			&nbsp;
		</div>
	</div>

	<a :id="sections.targetPotential.anchor" class="anchor-shift" />
	<div ref="targetPotentialRef" class="form-row row m-0 p-0 g-3">
		<div class="col-12">
			<label class="fw-bold mt-3">
				Цільовий діапазон захисного потенціалу
			</label>
		</div>

		<div class="col-12 col-xl-6">
			<div class="form-group my-1">
				<label>{{ Station.settingNames[Station.passportKeys.referencePotentialRangeMin] }}</label>
				<Field
					:name="Station.passportKeys.referencePotentialRangeMin"
					type="number"
					class="form-control form-control-sm no-arrow"
					:class="{ 'is-invalid': errors[Station.passportKeys.referencePotentialRangeMin] }"
				/>
				<div class="invalid-feedback">{{errors[Station.passportKeys.referencePotentialRangeMin]}}</div>
			</div>
			<div class="form-group my-1">
				<label>{{ Station.settingNames[Station.passportKeys.referencePotentialRangeMax] }}</label>
				<Field
					name="reference_potential_range_max"
					type="number"
					class="form-control form-control-sm no-arrow"
					:class="{ 'is-invalid': errors[Station.passportKeys.referencePotentialRangeMax] }"
				/>
				<div class="invalid-feedback">{{ errors[Station.passportKeys.referencePotentialRangeMax] }}</div>
			</div>
			<div class="form-group my-1">
				<Field
					:name="`${Station.passportKeys.notificationDistributionLists.self}.${Station.passportKeys.notificationDistributionLists.potentialOutOfRange}`"
					type="checkbox"
					class="form-check-input"
					:value="true"
					:uncheckedValue="false"
				/>
				{{ Station.settingNames[Station.passportKeys.notificationDistributionLists.self][Station.passportKeys.notificationDistributionLists.potentialOutOfRange] }}
			</div>
		</div>

		<div class="col-12 col-xl-6">
			<div class="bg-body border-radius px-3 py-2">
				<ol class="list-group list-group-flush list-group-numbered">
					<span class="form-text fst-italic fw-bold">Застосовується для</span>
					<li class="list-group-item">
						<span class="form-text fst-italic fw-bold">
							Визначення часу захисту споруди
						</span>
						<span class="form-text fst-italic text-break">
							В станціях з силовим модулем ВППС 2022 час захисту нараховується 
							тоді коли виміряний потенціал знаходиться у вказаному діапазоні. 
							Мінімальний&nbsp;потенціал&nbsp;&#8804;&nbsp;<b>Захисний&nbsp;потенціал</b>&nbsp;&#8804;&nbsp;Максимальний&nbsp;потенціал
						</span>
					</li>
					<li class="list-group-item">
						<span class="form-text fst-italic fw-bold">
							Надсилання сповіщень по виходу потенціала за вказаний діапазон
						</span>
					</li>
				</ol>
			</div>
		</div>
	</div>

	<a :id="sections.protectionStartTime.anchor" class="anchor-shift" />
	<div ref="protectionStartTimeRef" class="form-row row m-0 p-0 g-3">
		<div class="col-12">
			<label class="fw-bold mt-3">
				{{ PowerUnit.settingNames[PowerUnit.passportKeys.operaionAndProtectionStartDate] }}
			</label>
		</div>

		<div class="col-12 col-xl-6">
			<Field
				:name="Station.passportKeys.powerUnit.operaionAndProtectionStartDate"
				type="date"
				class="form-control form-control-sm no-arrow"
				:class="{
					'is-invalid': errors[Station.passportKeys.powerUnit.operaionAndProtectionStartDate]
				}"
				@change.stop="
					onChangeDate(
						Station.passportKeys.powerUnit.operaionAndProtectionStartDate,
						$event.target.value
					)
				"
			/>
			<div class="invalid-feedback">
				{{ errors[Station.passportKeys.powerUnit.operaionAndProtectionStartDate] }}
			</div>
		</div>

		<div class="col-12 col-xl-6" >
			<div class="bg-body border-radius px-3 py-2">
				<p class="form-text fst-italic paragraph-indent">
					В секції "Телеконтроль" метрики "Час захисту споруди" та "Загальний час напрацювання" будуть відраховуватись з вказаної дати.
				</p>
			</div>
		</div>
	</div>

	<div class="row row-cols-1 form-row m-0 p-0 my-3 g-3">
		<div class="col-12 my-0">
			<p v-if="feedbackPromt.length > 0" class="m-0" :class="feedbackPromtClass">
				{{ feedbackPromt }}
			</p>
			<p v-else-if="meta.dirty && meta.valid" class="text-success m-0">
				Натисніть "Зберегти" щоб примінити налаштування.
			</p>
			<p v-else-if="meta.dirty && !meta.valid" class="text-danger m-0">
				Є поля що заповнені некоректно.
			</p>
			<p v-else class="m-0">&nbsp;</p>
		</div>
	</div>

	<div class="row row-cols-1 form-row m-0 p-0 mb-2">
		<div class="col-12 my-0 mt-1">
			<button
				ref="submitButtonRef"
				type="submit"
				class="btn btn-primary btn-sm"
				:disabled="!meta.valid || !meta.dirty || isSubmitting"
			>
				Записати
			</button>
		</div>
	</div>
</fieldset>
</Form>
</template>




<script>
import { useStationsStore } from '@/stores/StationsStore.js'
import { useAccontStore } from '@/stores/AccountStore.js'
// Validation
// https://jasonwatmore.com/post/2020/10/01/vue-3-veevalidate-form-validation-example
import { Form, Field } from 'vee-validate'
// https://github.com/jquense/yup/tree/a58f02d2f6164c46a9757a818eebac582f7a441c#boolean
import * as Yup from 'yup';

import { Station } from '@/models/station.js'
import { PowerUnit } from '@/models/powerUnit.js'
import { ModemUnit } from '@/models/modemUnit.js'
import {
	parseTimeDate,
	parseApiTimeDate,
	convertToApiTimeDate,
} from '@/api/api.js'
import {
	capitalizeFirstLetter,
	dateTimeToString,
	dateTimeToDateString,
} from '@/helpers.js'


export default {
	requiredPromt: 'Обов\'язкове поле',
	overMinLenPromt: 'Довжина меньше мінімальної',
	overMaxLenPromt: 'Довжина більше максимальної',
	overMinPromt: 'Значення меньше мінімального',
	overMaxPromt: 'Значення більше максимального',
	onlyIntegersPromt: 'Має бути цілим числом',
	onlyPositivePromt: 'Має бути натуральним числом',
	wrongInputPromt: 'Не відповідає вимогам',

	components: {
		Form,
        Field,
	},

	props: {
		stationId: {
			type: String,
			require: true,
		},
	},

	emits: {
		sections: (s) => typeof s === "object",
		saveFunction: (f) => typeof f === 'function',
		saveFunctionActive: (f) => typeof f === 'boolean',
	},

	setup() {
		const stationsStore = useStationsStore()
		const accountStore = useAccontStore()

		return {
			stationsStore,
			accountStore,
			Station,
			PowerUnit,
			ModemUnit,
			capitalizeFirstLetter,
			parseTimeDate,
			dateTimeToString,
		}
	},

	data() {
		const schema = Yup.object().shape({
			[PowerUnit.passportKeys.unitVersion]: Yup.string()
				.typeError(this.$options.wrongInputPromt)
				.required(this.$options.requiredPromt),

			[PowerUnit.passportKeys.unitPort]: Yup.number()
				.typeError(this.$options.wrongInputPromt)
				.required(this.$options.requiredPromt)
				.integer(this.$options.onlyIntegersPromt)
				.min(1, this.$options.overMinPromt)
				.max(65535, this.$options.overMaxPromt),

			[PowerUnit.passportKeys.unitModbusAddr]: Yup.number()
				.typeError(this.$options.wrongInputPromt)
				.required(this.$options.requiredPromt)
				.integer(this.$options.onlyIntegersPromt)
				.min(1, this.$options.overMinPromt)
				.max(255, this.$options.overMaxPromt),

			[PowerUnit.passportKeys.operaionAndProtectionStartDate]: Yup.date()
				.nullable(true)
				.default(undefined)
				.typeError(this.$options.wrongInputPromt)
				.min(new Date(1900, 0, 1)),

			[Station.passportKeys.referencePotentialRangeMin]: Yup.lazy(
				(value) => (
					value === '' ?
					Yup.string() :
					Yup.number()
						.min(-5, this.$options.overMinPromt)
						.max(5, this.$options.overMaxPromt)
						.typeError(this.$options.wrongInputPromt)
			)),

			[Station.passportKeys.referencePotentialRangeMax]: Yup.lazy(
				(value) => (
					value === '' ?
					Yup.string() :
					Yup.number()
						.min(-5, this.$options.overMinPromt)
						.max(5, this.$options.overMaxPromt)
						.moreThan(Yup.ref(Station.passportKeys.referencePotentialRangeMin), this.$options.overMinPromt)
						.typeError(this.$options.wrongInputPromt)
			)),

			[Station.passportKeys.notificationDistributionLists.self]: Yup.object().shape({
				[Station.passportKeys.notificationDistributionLists.potentialOutOfRange]: Yup.boolean()
					.default(false),
			}),
		})

		return {
			// Form
			schema,
			formValues: {},
			formMeta: {},
			isSubmittingForm: false,

			// Feedback promt
			feedbackPromtClass: '',
			feedbackPromt: '',

			observer: null, // IntersectionObserver

			sections: {
				targetPotential: {
					anchor: 'target-potential',
					title: 'Цільовий потенціал',
					intersectionRatio: 0, // 0 .. 1
				},
				protectionStartTime: {
					anchor: 'protection-start-time',
					title: 'Час захисту споруди',
					intersectionRatio: 0, // 0 .. 1
				},
			},
		}
	},

	watch: {
		formMeta(newMeta) {
			if (newMeta) {
				this.$emit('saveFunctionActive', newMeta.valid && newMeta.dirty && !this.isSubmittingForm);
				// console.log('meta change', newMeta, !newMeta.valid || !newMeta.dirty || this.isSubmittingForm)
			}
		},
	},

	created() {
		this.updateForm()
	},

	mounted() {
		// Emit function
		this.$emit('saveFunction', this.submitButtonClick)

		// Add observer for viewport area feedback
		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					// console.log(`isIntersecting = ${entry.isIntersecting} intersectionRatio = ${entry.intersectionRatio}`)
					switch (entry.target) {
						case this.$refs.targetPotentialRef:
							this.sections.targetPotential.intersectionRatio = entry.intersectionRatio
							break
						case this.$refs.protectionStartTimeRef:
							this.sections.protectionStartTime.intersectionRatio = entry.intersectionRatio
							break
					}
				})
			},
			{
				root: null, // null means all viewport
				rootMargin: '0px',
				threshold: [0, 0.5, 1] // callback call trasholds 0%, 50% and 100% viewport
			}
		)

		this.observer.observe(this.$refs.targetPotentialRef)
		this.observer.observe(this.$refs.protectionStartTimeRef)

		this.$emit('sections', this.sections)
	},

	methods: {
		updateForm() {
			const passport = Object.fromEntries(this.station?.passport)
			const powerUnitPassport = this.station?.passport.get(Station.passportKeys.powerUnit.self)
			
			// power unit dates
			let time = powerUnitPassport[Station.passportKeys.powerUnit.operaionAndProtectionStartDate]
			if (time) {
				time = parseApiTimeDate(time)
				powerUnitPassport[Station.passportKeys.powerUnit.operaionAndProtectionStartDate] = dateTimeToDateString(time)
			}

			// If notification lists is present in passport
			let notificationsList = passport[Station.passportKeys.notificationDistributionLists.self]
			if (notificationsList) {
					notificationsList = {
						[Station.passportKeys.notificationDistributionLists.potentialOutOfRange]: 
							notificationsList[Station.passportKeys.notificationDistributionLists.potentialOutOfRange].includes(this.accountStore.userId)
					}
				}

			this.formValues = {
				...powerUnitPassport,
				[Station.passportKeys.referencePotentialRangeMin]: passport[Station.passportKeys.referencePotentialRangeMin] ?? '',
				[Station.passportKeys.referencePotentialRangeMax]: passport[Station.passportKeys.referencePotentialRangeMax] ?? '',
				[Station.passportKeys.notificationDistributionLists.self]: notificationsList,
			}
		},

		onSubmit(values) {
			// console.log("onSubmit")
			const payload = {}//{ ...values }
			payload[Station.passportKeys.powerUnit.self] = { ...values }
			delete payload[Station.passportKeys.powerUnit.self][Station.passportKeys.notificationDistributionLists.self]
			delete payload[Station.passportKeys.powerUnit.self][Station.passportKeys.referencePotentialRangeMin]
			delete payload[Station.passportKeys.powerUnit.self][Station.passportKeys.referencePotentialRangeMax]
			payload[Station.passportKeys.referencePotentialRangeMin] = values[Station.passportKeys.referencePotentialRangeMin]
			payload[Station.passportKeys.referencePotentialRangeMax] = values[Station.passportKeys.referencePotentialRangeMax]
			payload[Station.passportKeys.notificationDistributionLists.self] =
				{ ...values[Station.passportKeys.notificationDistributionLists.self] }
			
			// console.log("values", values)

			// Enumerate all schema and delete empty fields
			for (const [key, ] of Object.entries(this.schema.fields)) {
				// Delete all empty strings
				if (typeof values[key] == 'string' && values[key]?.length == 0) {
					payload[key] = null
				}

				// Delete all unefined fields
				if (payload[key] === undefined) {
					delete payload[key]
				}
			}
			
			for (const [key, value] of Object.entries(values)) {
				// Delete all empty strings
				if (value?.type == 'string' && value.length == 0) {
					delete payload[Station.passportKeys.powerUnit.self][key]
				}

				// Delete all unefined fields
				if (payload[Station.passportKeys.powerUnit.self][key] === undefined) {
					delete payload[Station.passportKeys.powerUnit.self][key]
				}
			}

			// Parse notification distribution lists and convert to passport format
			payload[Station.passportKeys.notificationDistributionLists.self] = this.station.passport.get(Station.passportKeys.notificationDistributionLists.self)
			const userId = this.accountStore.userId
			for (const [key, value] of Object.entries(values[Station.passportKeys.notificationDistributionLists.self])) {
				// If notification is enable
				if (value) {
					// if no list at all
					if (payload[Station.passportKeys.notificationDistributionLists.self][key] === undefined) {
						payload[Station.passportKeys.notificationDistributionLists.self][key] = []
					}
					// If current user is not in distribution notifications list
					if (!payload[Station.passportKeys.notificationDistributionLists.self][key].includes(userId)) {
						payload[Station.passportKeys.notificationDistributionLists.self][key].push(userId)
					}
				} else {
					// If current user is in distribution notifications list
					if (payload[Station.passportKeys.notificationDistributionLists.self][key] !== undefined
						&& payload[Station.passportKeys.notificationDistributionLists.self][key].includes(userId)
					) {
						const index = payload[Station.passportKeys.notificationDistributionLists.self][key].indexOf(userId);
						payload[Station.passportKeys.notificationDistributionLists.self][key].splice(index, 1)
					}
				}
			}

			// Convert power unit nested date
			let time = values[Station.passportKeys.powerUnit.operaionAndProtectionStartDate]
			if (time) {
				let date = new Date()
				date.setHours(0, 0, 0, 0)
				date.setFullYear(Number(time.slice(0, 4)), Number(time.slice(5, 7)) - 1, Number(time.slice(8, 10)))
				payload[Station.passportKeys.powerUnit.self][Station.passportKeys.powerUnit.operaionAndProtectionStartDate] =
					convertToApiTimeDate(date)
			}

			payload[Station.passportKeys.updateBy] = this.accountStore.userId
			
			// console.log("payload", payload)
			
			this.stationsStore.updatePasport(
				(result) => {
					if (result) {
						this.printHint('Паспорт успішно оновлено.', 'passport')
						// Update passport
						this.stationsStore.readPassport(
							(result) => {
								if (result) {
									this.updateForm()
								}
							},
							this.stationId
						)
					} else {
						this.printErr('Сервер відхилив запит.', 'passport')
					}
				},
				this.stationId,
				payload,
			)
		},

		submitButtonClick() {
			this.$refs.submitButtonRef.click()
		},

		// Delete date field if date is cleared by button "Clear"
		onChangeDate(key, newDate) {
			if (newDate == "") {
				this.$refs.form.setFieldValue(key, null);
			}
		},

		printHint (msg) {
			this.feedbackPromtClass = "text-success"
			this.feedbackPromt = msg
		},

		printErr (msg) {
			this.feedbackPromtClass = "text-danger"
			this.feedbackPromt = msg
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },

		isFormEnabled() { return this.accountStore.permissions.includes('manage_stations') },
	},

	beforeUnmount() {
		// If observer, then disable
		if (this.observer) {
			this.observer.disconnect()
		}
	}
}
</script>




<style scoped>
/* Remove arrows from input Chrome, Safari, Edge, Opera */
input.no-arrow::-webkit-outer-spin-button,
input.no-arrow::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* Remove arrows from input Firefox */
input[type=number].no-arrow {
	appearance: textfield;
	-moz-appearance: textfield;
}

/* Style for input if it is not match register value */
.not-match-register {
	border-color: rgb(11, 94, 215);
	color: rgb(11, 94, 215);
}
</style>